import React, { useState } from 'react';
import { Box, Popover, TextField, Typography } from '@material-ui/core';
import { Schedule as ScheduleIcon } from '@material-ui/icons';
import { unpackToTime } from '@campfire/hot-date';
import { TabletButton } from '@campfire/tablet-button';

interface Props {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  sessionStartTime: string | null;
  sessionEndTime: string | null;
  currentStartTime: string | null;
  currentEndTime: string | null;
  onApply: (startTime: string | null, endTime: string | null) => void;
  onRemove: () => void;
  volunteerName: string;
  sessionName: string;
}

function formatTimeForInput(time: string | null): string {
  if (!time) return '';
  return unpackToTime(time).toFormat('HH:mm');
}

function formatTimeForDisplay(time: string | null): string {
  if (!time) return '';
  return unpackToTime(time).toFormat('h:mm a');
}

export function RosterTimePopover({
  anchorEl,
  onClose,
  sessionStartTime,
  sessionEndTime,
  currentStartTime,
  currentEndTime,
  onApply,
  onRemove,
  volunteerName,
  sessionName,
}: Props) {
  const [startTime, setStartTime] = useState<string>(formatTimeForInput(currentStartTime));
  const [endTime, setEndTime] = useState<string>(formatTimeForInput(currentEndTime));

  const handleApply = () => {
    onApply(startTime || null, endTime || null);
    onClose();
  };

  const handleClear = () => {
    setStartTime('');
    setEndTime('');
    onApply(null, null);
    onClose();
  };

  const handleRemove = () => {
    onRemove();
    onClose();
  };

  const hasCustomTimes = startTime !== '' || endTime !== '';

  // Validate that end time is not before start time
  const isInvalidTimeRange = startTime !== '' && endTime !== '' && endTime < startTime;

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <Box p={2} width={280}>
        <Box display='flex' alignItems='center' mb={1}>
          <ScheduleIcon style={{ fontSize: 18, marginRight: 6, color: '#808080' }} />
          <Typography variant='subtitle2' style={{ fontWeight: 600, fontSize: 13 }}>
            Shift Times
          </Typography>
        </Box>
        <Typography variant='body2' style={{ fontSize: 12, color: '#808080', marginBottom: 8 }}>
          {volunteerName} &mdash; {sessionName}
        </Typography>
        <Typography variant='body2' style={{ fontSize: 11, color: '#a0a0a0', marginBottom: 12 }}>
          Session default: {formatTimeForDisplay(sessionStartTime)} - {formatTimeForDisplay(sessionEndTime)}
        </Typography>

        <Box display='flex' alignItems='center' mb={1.5}>
          <TextField
            label='Start'
            type='time'
            size='small'
            variant='outlined'
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
            style={{ flex: 1, marginRight: 8 }}
          />
          <TextField
            label='End'
            type='time'
            size='small'
            variant='outlined'
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
            style={{ flex: 1 }}
            error={isInvalidTimeRange}
          />
        </Box>
        {isInvalidTimeRange && (
          <Typography variant='body2' style={{ fontSize: 11, color: '#F44336', marginBottom: 8 }}>
            End time cannot be before start time
          </Typography>
        )}

        <Box display='flex' justifyContent='space-between' alignItems='center' mt={1}>
          <TabletButton
            size='small'
            onClick={handleRemove}
            style={{
              color: '#F44336',
              borderColor: '#F44336',
              textTransform: 'none',
              fontSize: 12,
            }}
            variant='outlined'
          >
            Remove
          </TabletButton>
          <Box>
            {hasCustomTimes && (
              <TabletButton
                size='small'
                onClick={handleClear}
                style={{
                  textTransform: 'none',
                  fontSize: 12,
                  marginRight: 6,
                }}
              >
                Clear
              </TabletButton>
            )}
            <TabletButton
              size='small'
              variant='contained'
              color='primary'
              onClick={handleApply}
              disabled={isInvalidTimeRange}
              style={{
                textTransform: 'none',
                fontSize: 12,
              }}
            >
              Apply
            </TabletButton>
          </Box>
        </Box>
      </Box>
    </Popover>
  );
}
